/**
 * Perceptual readability measures — pack-agnostic pixel metrics.
 *
 * Geometry gates (bbox/keys/hamming) are satisfiable by flat geometric blobs
 * (Goodhart). These measures target the "reads as a rectangle/circle" failure
 * class directly: flat single-key masses, missing tonal modelling, stray
 * pixel noise. Thresholds live in the Art Lab façade (character-qc), not here.
 */

export type RectReadReport = {
  /** Longest horizontal run of one opaque key. */
  maxRun: number;
  /** Largest same-key solid square (side length). */
  maxBlock: number;
};

/** Flat-mass detector: long single-key runs / solid blocks read as geometry. */
export function measureRectRead(rows: string[]): RectReadReport {
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  let maxRun = 0;
  for (let y = 0; y < h; y++) {
    let run = 0;
    let prev = ".";
    for (let x = 0; x <= w; x++) {
      const ch = x < w ? rows[y]![x]! : ".";
      if (ch !== "." && ch === prev) {
        run++;
      } else {
        if (prev !== ".") maxRun = Math.max(maxRun, run);
        run = 1;
      }
      prev = ch;
    }
  }
  // Largest same-key square via DP: side[y][x] = min(top, left, topleft) + 1.
  let maxBlock = 0;
  let prevRow = new Array<number>(w).fill(0);
  for (let y = 0; y < h; y++) {
    const cur = new Array<number>(w).fill(0);
    for (let x = 0; x < w; x++) {
      const ch = rows[y]![x]!;
      if (ch === ".") continue;
      const same = (yy: number, xx: number): boolean =>
        yy >= 0 && xx >= 0 && rows[yy]![xx] === ch;
      if (x === 0 || y === 0) {
        cur[x] = 1;
      } else {
        const top = same(y - 1, x) ? prevRow[x]! : 0;
        const left = same(y, x - 1) ? cur[x - 1]! : 0;
        const diag = same(y - 1, x - 1) ? prevRow[x - 1]! : 0;
        cur[x] = Math.min(top, left, diag) + 1;
      }
      maxBlock = Math.max(maxBlock, cur[x]!);
    }
    prevRow = cur;
  }
  return { maxRun, maxBlock };
}

export type ToneBandsReport = {
  /** Unique non-selout opaque keys per vertical bbox third (top/mid/bottom). */
  bands: [number, number, number];
};

/** Tonal modelling check: every body third must carry ≥2 tones (no flat fill). */
export function measureToneBands(
  rows: string[],
  seloutKey: string,
): ToneBandsReport {
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  let top = -1;
  let bottom = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (rows[y]![x] !== ".") {
        if (top < 0) top = y;
        bottom = y;
        break;
      }
    }
  }
  const bands: [number, number, number] = [0, 0, 0];
  if (top < 0) return { bands };
  const span = bottom - top + 1;
  const sets = [new Set<string>(), new Set<string>(), new Set<string>()];
  for (let y = top; y <= bottom; y++) {
    const band = Math.min(2, Math.floor(((y - top) / span) * 3));
    for (let x = 0; x < w; x++) {
      const ch = rows[y]![x]!;
      if (ch !== "." && ch !== seloutKey) sets[band]!.add(ch);
    }
  }
  return { bands: [sets[0]!.size, sets[1]!.size, sets[2]!.size] };
}

/** Opaque pixels with all-8 transparent neighbors (stray noise / jitter). */
export function countIsolatedPixels(rows: string[]): number {
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  let n = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (rows[y]![x] === ".") continue;
      let alone = true;
      for (let dy = -1; dy <= 1 && alone; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const yy = y + dy;
          const xx = x + dx;
          if (yy >= 0 && yy < h && xx >= 0 && xx < w && rows[yy]![xx] !== ".") {
            alone = false;
            break;
          }
        }
      }
      if (alone) n++;
    }
  }
  return n;
}
